import { memo, ReactNode, useState } from 'react';

import {
  Box,
  createStyles,
  FormControl,
  Link,
  makeStyles,
  TextField,
} from '@material-ui/core';

import { validateTenantName } from '../validation';

const useStyles = makeStyles(() =>
  createStyles({
    formControl: {
      display: 'flex',
      flexDirection: 'row',
    },
    inputBox: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
    },
    inputElement: {
      flex: '1 1 auto',
    },
    inputElementInput: {
      height: '30px',
    },
    textBefore: (props: { textBefore: string }) =>
      props.textBefore
        ? {
            '&::before': {
              content: `"${props.textBefore}"`,
              paddingBottom: '4px',
              color: 'gray',
            },
            '& input': {
              paddingLeft: '70px',
            },
          }
        : {},
  }),
);

const TenantTextInput = ({
  id,
  value,
  name,
  onChange,
  helperText,
  required,
  disabled,
  error,
  textBefore,
}: {
  id: string;
  value: string;
  name: string;
  onChange: (value: any) => void;
  helperText?: string | ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  textBefore?: string | null;
}) => {
  const classes = useStyles({ textBefore: textBefore || '' });
  return (
    <FormControl fullWidth className={classes.formControl}>
      <Box className={classes.inputBox}>
        <TextField
          id={id}
          name={name}
          label="Name"
          className={classes.inputElement}
          required={required}
          disabled={disabled}
          error={error}
          value={value}
          size="medium"
          InputLabelProps={{ shrink: true }}
          onChange={onChange}
          onBlur={onChange}
          variant="standard"
          InputProps={{
            className: `${classes.inputElementInput} ${
              textBefore ? classes.textBefore : ''
            }`,
          }}
          FormHelperTextProps={{ disabled: false }}
          helperText={disabled ? '' : helperText}
        />
      </Box>
    </FormControl>
  );
};

type TenantNameSectionProps = {
  value: string;
  stage: string;
  tenantId?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
};

export const TenantNameSection = memo(
  ({
    value,
    stage,
    tenantId,
    onChange,
    disabled,
    required = true,
  }: TenantNameSectionProps) => {
    const [nameError, setNameError] = useState('');

    const onNameChange = (e: any) => {
      setNameError(validateTenantName(e.target.value));
      onChange(e.target.value);
    };

    return (
      <FormControl fullWidth required={required}>
        <TenantTextInput
          id="tenant-name"
          name="name"
          value={value ?? ''}
          required={required}
          disabled={disabled}
          onChange={onNameChange}
          textBefore={tenantId ? '' : `${stage}-${tenantId ?? '****'}-`}
          error={Boolean(nameError)}
          helperText={
            nameError || (
              <span>
                For example: {stage}-****-secrets-sync, where **** is reserved
                for tenant ID.{' '}
                <Link
                  target="_blank"
                  href="https://frontstage.tomtomgroup.com/docs/default/component/kaap-documentation/#tenants"
                >
                  More details.
                </Link>
              </span>
            )
          }
        />
      </FormControl>
    );
  },
);
